import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataItem } from '../../services/apiSLTP/activities';
import { createReportIfEmpty, updateActivity } from '../../services/apiSLTP/planning';
import { User } from '../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { activityStateEnum, activityTypeEnum, permissionLevels, reportStateEnum, reportTypeEnum } from '../../utils/enums';
import CustomToggle from '../CustomToggle';
import DropDown from '../DropDown';
import { useAuth } from '../../hooks/useAuth';

interface PersonNeedsCellProps {
    activity: DataItem;
    users?: User[];
    disabled?: boolean;
    activities: DataItem[];
    refetch: () => void;
    loading?: boolean;
}

const PersonNeedsCell: React.FC<PersonNeedsCellProps> = React.memo(({ activity, users, disabled = false, activities, refetch, loading }) => {
    const [usersFiltered, setUsersFiltered] = useState(true);

    const { t } = useTranslation();
    const { user } = useAuth();

    const moTruckId = '66b097d4b0a672302822c715';
    const moAmplirollId = '66e14538c7639ac65e2e6763';

    const getReport = async (user: { label: string; value: string }) => {
        const response = await createReportIfEmpty(user.value, activity.startTime, reportStateEnum.PLANNING);
        return response.data;
    };

    const countDuplicatePerson = (userId: string) => {
        let duplicateCount = 0;
        for (const activity of activities) {
            if (activity.reportId.userId._id === userId && !activity.reportId.userId.isSpecial) {
                duplicateCount++;
            }
        }
        return duplicateCount;
    };

    const handleUserChange = async (user: { label: string; value: string }) => {
        const tmpUser = users?.find((value) => value._id === user.value);
        const isUserTruck = [moAmplirollId, moTruckId].includes(tmpUser?._id || '');
        const isActivityTypeTruck = activity.activityTypeId.type === activityTypeEnum.TRUCK;

        const report = await getReport(user);
        const response = await updateActivity(activity._id, { reportId: report._id });

        refetch();
    };

    const selectedUser = () => {
        return {
            value: activity.reportId.userId._id,
            label: formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName),
        };
    };

    const filterUsers = () => {
        return (
            (usersFiltered
                ? users?.filter((user) => user.profileId.permsLevel === activity.reportId.userId.profileId.permsLevel)
                : users?.filter((user) => user.profileId.permsLevel >= permissionLevels.TERRAIN)) || []
        );
    };

    return (
        <div
            className="fx-r fx-items-c"
            title={selectedUser().label}
            onKeyDown={(event) => {
                if (event.ctrlKey) {
                    setUsersFiltered(!usersFiltered);
                }
            }}
        >
            {activity.siteId.conducId?._id === user?._id || activity.siteId.assistantConducId?._id === user?._id ? (
                <DropDown
                    selectedItem={selectedUser()}
                    items={filterUsers()
                        .filter((value) =>
                            [moAmplirollId, moTruckId].includes(selectedUser().value)
                                ? [moAmplirollId, moTruckId].includes(value._id)
                                : ![moAmplirollId, moTruckId].includes(value._id)
                        )
                        .map((user) => ({
                            label: formatName(user.firstName, user.lastName),
                            value: user?._id,
                        }))}
                    inputLabel=""
                    handleChange={(event, value) => handleUserChange({ value: value?.value || '', label: value?.label || '' })}
                    disabled={disabled}
                    formClassName="pad-0-imp"
                    loading={loading}
                    warning={countDuplicatePerson(activity.reportId.userId._id) > 1}
                    warningChar={countDuplicatePerson(activity.reportId.userId._id).toString()}
                    infos={
                        (activity.reportId.userId.isSpecial && activity.activityState === activityStateEnum.NEEDS) ||
                        activity.reportId.reportType === reportTypeEnum.ABSENT
                    }
                    infosColor={activity.reportId.reportType === reportTypeEnum.ABSENT ? 'yellow' : undefined}
                />
            ) : (
                <span>{selectedUser().label}</span>
            )}

            <div title={capitalizeFirstLetter(t('absent'))}>{activity.reportId.reportType === reportTypeEnum.ABSENT && '❗'}</div>
        </div>
    );
});

export default PersonNeedsCell;
